type MentionRecord = Record<string, unknown>;

export type UniqueBrandMention = {
  key: string;
  brand: string;
};

export type BrandMentionCount = {
  brand: string;
  count: number;
};

export type BrandMentionFrequencyBucket = {
  brand: string;
  canonicalKey: string;
  displayName: string;
  count: number;
  rawNames: string[];
  isTargetBrand: boolean;
};

const MENTION_LABEL_FIELDS = [
  "display_name",
  "raw_entity_name",
  "normalized_entity_name",
  "brand",
  "name",
  "entity",
  "entity_name",
  "normalized_brand"
] as const;

function getMentionLabel(value: unknown): string {
  if (typeof value === "string") {
    return value.trim();
  }

  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return "";
  }

  const record = value as MentionRecord;

  for (const field of MENTION_LABEL_FIELDS) {
    const candidate = record[field];

    if (typeof candidate === "string" && candidate.trim()) {
      return candidate.trim();
    }
  }

  return "";
}

export function normalizeBrandMentionKey(value: unknown): string {
  return String(value || "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[™®]/g, "")
    .replace(/[^a-z0-9]/g, "");
}

export function extractBrandMentions(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map(getMentionLabel).filter(Boolean);
  }

  if (typeof value !== "string") {
    return [];
  }

  const trimmed = value.trim();

  if (!trimmed) {
    return [];
  }

  try {
    const parsed = JSON.parse(trimmed);

    if (Array.isArray(parsed)) {
      return parsed.map(getMentionLabel).filter(Boolean);
    }
  } catch {
    // Some older rows contain comma-separated values rather than JSON.
  }

  return trimmed
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

/**
 * Returns each normalized brand at most once for one prompt response.
 *
 * This prevents spelling, capitalization, punctuation, or duplicate extraction
 * variants within the same response from inflating visibility totals.
 */
export function uniqueBrandMentions(value: unknown): UniqueBrandMention[] {
  const unique = new Map<string, string>();

  extractBrandMentions(value).forEach((brand) => {
    const key = normalizeBrandMentionKey(brand);

    if (!key || unique.has(key)) {
      return;
    }

    unique.set(key, brand);
  });

  return Array.from(unique.entries()).map(([key, brand]) => ({
    key,
    brand
  }));
}

/**
 * Counts the number of responses containing each brand.
 *
 * A brand receives no more than one count for any individual response.
 */
export function aggregateBrandMentionsByResponse<T>(
  rows: readonly T[],
  getBrandMentions: (row: T) => unknown
): Map<string, BrandMentionCount> {
  const counts = new Map<string, BrandMentionCount>();

  rows.forEach((row) => {
    uniqueBrandMentions(getBrandMentions(row)).forEach(({ key, brand }) => {
      const existing = counts.get(key);

      if (existing) {
        existing.count += 1;
      } else {
        counts.set(key, {
          brand,
          count: 1
        });
      }
    });
  });

  return counts;
}

export function aggregateBrandMentionsForFrequency<T>(
  rows: readonly T[],
  getBrandMentions: (row: T) => unknown,
  customerBrand?: string | null
): BrandMentionFrequencyBucket[] {
  const counts = aggregateBrandMentionsByResponse(rows, getBrandMentions);
  const customerKey = normalizeBrandMentionKey(customerBrand);

  return Array.from(counts.entries())
    .map(([key, item]) => ({
      brand: item.brand,
      canonicalKey: key,
      displayName: item.brand,
      count: item.count,
      rawNames: [item.brand],
      isTargetBrand: Boolean(customerKey && key === customerKey)
    }))
    .sort(
      (left, right) =>
        right.count - left.count ||
        left.displayName.localeCompare(right.displayName, undefined, { sensitivity: "base" })
    );
}

//
// Keep competitive ranking organic-only regardless of caller-side query filters.
// Guided prompts explicitly name the target brand, so including them inflates
// both the target's frequency and its apparent competitive position.
export function aggregateOrganicBrandMentionsForFrequency<T extends {
  prompt_type?: string | null;
  status?: string | null;
  visibility_signal?: string | null;
}>(
  rows: readonly T[],
  getBrandMentions: (row: T) => unknown,
  customerBrand?: string | null
): BrandMentionFrequencyBucket[] {
  const organicCompletedRows = rows.filter(
    (row) =>
      row.prompt_type === "organic" &&
      row.status === "completed" &&
      row.visibility_signal !== "error"
  );

  return aggregateBrandMentionsForFrequency(
    organicCompletedRows,
    getBrandMentions,
    customerBrand
  );
}
